'use client'
import React, { useEffect, useRef, useState } from 'react'
// import { useSession } from 'next-auth/react'
import Image from 'next/image'
import LinkComponent from './LinkComponent'
import { BiArea, BiSolidArea } from "react-icons/bi";
import { FaBed, FaBath, FaWater } from "react-icons/fa"
import { MdBeachAccess, MdPlaylistAddCheckCircle } from "react-icons/md";
import { IoMdVideocam } from 'react-icons/io'
import tailwindConfig from '@tailwind.config'
import CurrencySymbol from '@components/CurrencySymbol'
import { sendGAEvent } from '@next/third-parties/google'

function GallerySlider({ images, alt, blur }) {
    const [index, setIndex] = useState(0)
    const [hovered, setHovered] = useState(false)
    const touchStart = useRef(null)
    const total = images?.length ?? 0
    if (!total) return null
    const prev = (e) => { e.preventDefault(); e.stopPropagation(); setIndex(i => (i - 1 + total) % total) }
    const next = (e) => { e.preventDefault(); e.stopPropagation(); setIndex(i => (i + 1) % total) }
    const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX }
    const onTouchEnd = (e) => {
        if (touchStart.current === null) return
        const delta = touchStart.current - e.changedTouches[0].clientX
        if (Math.abs(delta) > 40) delta > 0 ? setIndex(i => (i + 1) % total) : setIndex(i => (i - 1 + total) % total)
        touchStart.current = null
    }
    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {images.map((src, i) => (
                <div key={i} style={{ position: 'absolute', inset: 0, opacity: i === index ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: i === index ? 'auto' : 'none' }}>
                    <Image placeholder={i === 0 && blur ? 'blur' : 'empty'} blurDataURL={i === 0 ? blur : undefined} src={src || '/images/imageplaceholder.jpeg'} fill alt={`${alt} ${i + 1}`} style={{ objectFit: 'cover' }} />
                </div>
            ))}
            {total > 1 && (
                <>
                    <button onClick={prev} style={{ position: 'absolute', insetInlineStart: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 4, background: 'rgba(0,0,0,0.35)', border: 'none', borderRadius: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', backdropFilter: 'blur(4px)', padding: 0, opacity: 1, transition: 'opacity 0.2s ease' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                    <button onClick={next} style={{ position: 'absolute', insetInlineEnd: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 4, background: 'rgba(0,0,0,0.35)', border: 'none', borderRadius: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', backdropFilter: 'blur(4px)', padding: 0, opacity: 1, transition: 'opacity 0.2s ease' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                    </button>
                    <div style={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 5, zIndex: 4, opacity: hovered ? 1 : 0, transition: 'opacity 0.2s ease' }}>
                        {images.map((_, i) => <button key={i} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIndex(i) }} style={{ width: i === index ? 16 : 6, height: 6, borderRadius: 3, border: 'none', padding: 0, cursor: 'pointer', background: i === index ? '#fff' : 'rgba(255,255,255,0.5)', transition: 'all 0.2s ease' }} />)}
                    </div>
                    <div style={{ position: 'absolute', top: 8, insetInlineStart: 8, zIndex: 4, background: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px', borderRadius: 20, backdropFilter: 'blur(4px)', letterSpacing: '0.04em', opacity: hovered ? 1 : 0, transition: 'opacity 0.2s ease' }}>
                        {index + 1} / {total}
                    </div>
                </>
            )}
        </div>
    )
}

const PropertyCard = ({ property, imageWidth = 400, imageHeight = 300, newLimit, isLast, cardType = undefined, dictionary, lang }) => {
    const cardRef = useRef();

    useEffect(() => {
        if (!cardRef?.current) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (isLast && entry.isIntersecting) {
                newLimit();
                // nextPage()
                observer.unobserve(entry.target);
            }
        });

        observer.observe(cardRef.current);
    }, [isLast]);

    const galleryImages = (property.gallery && property.gallery.length > 0)
        ? property.gallery
        : [property.cover]

    return (
        <div className='relative w-full m-1 rounded-lg shadow-md hover:shadow-sm hover:shadow-maincolor'
            ref={cardRef}
            onClick={() => sendGAEvent({ 
                event: "select_property",
                property_id: property.ref,
                property_name: property.name,
                property_type: property.metaCategory,
                property_location: property.districts?.[0]?.subdistrictName?.['en'],
            })} 
        >
            <LinkComponent
                className='w-full'
                href={'/properties/' + property.slug}
            >
                <div className='relative w-full aspect-[4/3] rounded-t-md overflow-hidden'>
                    <GallerySlider
                        images={galleryImages}
                        alt={property.slug}
                        blur={property.blur}
                    />
                        {(property.videoUrl || property.isSeaView || property.isBeachfront || property.isMountainView || property.foreignQuota) ? (
                            <div className='absolute w-full bottom-1 left-1 flex flex-row flex-wrap z-10 pointer-events-none'>
                                {property.videoUrl ? (
                                    <div className={`rounded-md p-1 text-light bg-red-800 text-sm mr-1 flex flex-row items-center`}><IoMdVideocam /><span className='ml-1'>{dictionary?.video}</span></div>
                                ) : null}
                                {property.isSeaView ? (
                                    <div className={`rounded-md p-1 bg-maincolor text-light border-light border-[1px] text-sm mr-1 flex flex-row items-center`}><FaWater /><span className='ml-1'>{dictionary?.seaView}</span></div>
                                ) : null}
                                {property.isBeachfront ? (
                                    <div className={`rounded-md p-1 bg-third text-light border-third border-[1px] text-sm mr-1 flex flex-row items-center`}><MdBeachAccess /><span className='ml-1'>{dictionary?.beachfront}</span></div>
                                ) : null}
                            </div>
                        ) : null}
                    {property.publicTransport ? (
                        <div className='absolute flex flex-row flex-wrap bottom-0 left-0 z-10 pointer-events-none'>
                            {property.publicTransport.map((transport, index) => (
                                <div key={index} style={{ backgroundColor: transport.colorCode }} className={`rounded-sm px-1 text-light text-sm ml-1 mb-1`}>{transport.type.toUpperCase()} {transport.stationName}</div>
                            ))}
                        </div>
                    ) : null}
                </div>
                {cardType == 'new' && (
                    <div className={`absolute left-2 top-2 bg-third text-light p-1 text-md rounded-md z-10`}><span>{dictionary.listedAt}</span></div>
                )}
                {cardType == 'promotion' && (
                    <div className={`absolute left-2 top-2 bg-third text-light p-1 text-md rounded-md z-10`}><span>{property.promotionTitle}</span></div>
                )}
                <div className='p-2'>
                    <div className='text-maincolor text-xl font-semibold rounded-r-md z-10 h-[60px]'><span className='line-clamp-2'>{property.name}</span></div>
                    <div className='flex flex-col w-full h-[60px] items-start'>
                        {property.salePrice && (
                            <div className='w-full flex flex-row justify-between items-center'>
                                <div className='px-2 bg-maincolor text-white font-bold rounded-md'>{dictionary.sale}</div>
                                <div className='text-xl font-semibold text-third'><CurrencySymbol />{property.salePrice}</div>
                            </div>
                        )}
                        {property.rentPrice && (
                            <div className='w-full flex flex-row justify-between items-center'>
                                <div className='px-2 bg-maincolor/10 border border-maincolor text-maincolor font-bold rounded-md'>{dictionary.rent}</div>
                                <div className='text-xl font-semibold text-third'><CurrencySymbol />{property.rentPrice}/{dictionary.month}</div>
                            </div>
                        )}
                    </div>
                    <div className='w-full border-b-[1px] border-forth pb-1 mb-1'>
                        <p className='text-secondary text-xl'>{property?.districts?.[0]?.subdistrictName?.[lang] ? property.districts[0]?.subdistrictName?.[lang] : property.districts?.[0]?.subdistrict}</p>
                    </div>
                    {(property.metaCategory == 'Condo' || property.metaCategory == 'House' || property.metaCategory == 'Poolvilla' || property.metaCategory == 'Commercial') &&
                        <div className='flex flex-row w-full'>
                            <div className='flex flex-row items-center w-1/2'>
                                <FaBed
                                    color={tailwindConfig.theme.extend.colors.secondary}
                                />
                                {(property.bedrooms && (property.bedrooms > 0 || typeof property.bedrooms == 'string')) ? (
                                    <span className='ml-2 text-xl text-maincolor'>{property.bedrooms} {typeof property.bedrooms !== 'string' ? dictionary.beds : ''}</span>
                                ):null}
                            </div>
                            {(property.bathrooms && property.bathrooms > 0) ? (
                                <div className='flex flex-row items-center w-1/2'>
                                    <FaBath
                                        color={tailwindConfig.theme.extend.colors.secondary}
                                    />
                                    <span className='ml-2 text-xl text-maincolor'>{property.bathrooms} {dictionary.baths}</span>
                                </div>
                            ):null}
                        </div>

                    }
                    <div className='flex flex-row w-full justify-between min-h-7'>
                        {(property.livingArea !== undefined && property.livingArea > 0) ? (
                            <div className='flex flex-row items-center w-1/2'>
                                <BiArea
                                    color={tailwindConfig.theme.extend.colors.secondary}
                                />
                                <span className='ml-2 text-xl text-maincolor'>{property.livingArea} {dictionary.sqm}</span>
                            </div>
                        ): null}
                        {(property.landArea !== undefined && property.landArea > 0) && (
                            <div className='flex flex-row items-center w-1/2'>
                                <BiSolidArea
                                    color={tailwindConfig.theme.extend.colors.secondary}
                                />
                                <span className='ml-2 text-xl text-maincolor'>{property.landArea} {dictionary.sqm}</span>
                            </div>
                        )}
                    </div>
                </div>
            </LinkComponent >
        </div>
    )
}

export default PropertyCard